/**
 * Platform Services API module (Cloud Accounts, Resources, Dashboard,
 * Inventory, RBAC, Analytics, Admin, SSO/MFA, Remediation, Notifications,
 * Workflows, Projects, Reports, Tenants, API Keys, OAuth, SSO Group Mapping).
 *
 * Real per-domain split from services/api.ts. Verbatim extraction of the 30
 * platform API objects; behavior is unchanged. Other adjacent platform-style
 * exports (e.g. siemApi, adminApi-style helpers, dataResidencyApi) remain in
 * api.ts and are re-exported by services/api/index.ts.
 */
import { _unwrap, _unwrapWithOpts, _thenUnwrap } from './_unwrap';
// `apiClient` is a NAMED export from ./client (verified against vulnerability/ai/easm
// sibling splits). The earlier default-import form would fail at runtime.
import { apiClient as fetchClient } from './client';
import {
  CloudAccount,
  CloudProvider,
  CloudResource,
  DashboardStats,
  DiscoveryJob,
  PaginatedResponse,
} from '../../types';

// ============================================================================
// Cloud Accounts API (Core Service)
// ============================================================================

/**
 * What GET /api/v1/cloud-accounts actually returns.
 *
 * NOT `PaginatedResponse<CloudAccount>`. That declaration named `items` /
 * `has_more`, and the endpoint emits neither — it answers
 * `{total, connectors[], limit, offset}`. Three separate call sites each
 * independently reached for `items` / `accounts` / `cloud_accounts` on the
 * strength of the declared type and each silently resolved to `[]`:
 * CloudAccountsDashboard rendered "no accounts" over a connected AWS account,
 * the CSPM account selector was permanently empty, and InventoryDashboard's
 * post-scan wait loop broke on its first iteration. Curl 2026-07-31
 * (?page_size=100): keys = total, connectors, limit, offset; total=1,
 * len(connectors)=1; items/accounts/cloud_accounts all absent.
 *
 * Typed honestly here so the next caller cannot repeat the mistake.
 */
export interface CloudAccountsListResponse {
  connectors: CloudAccount[];
  total: number;
  limit?: number;
  offset?: number;
}

export const cloudAccountsApi = {
  list: async (params?: {
    provider?: CloudProvider;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<CloudAccountsListResponse> => {
    return _unwrap(await fetchClient.get<CloudAccountsListResponse>('/api/v1/cloud-accounts', params as any));
  },

  get: async (id: string): Promise<CloudAccount> => {
    return _unwrap(await fetchClient.get<CloudAccount>(`/api/v1/cloud-accounts/${id}`));
  },

  create: async (data: Partial<CloudAccount>): Promise<CloudAccount> => {
    return _unwrap(await fetchClient.post<CloudAccount>('/api/v1/cloud-accounts', data));
  },

  update: async (id: string, data: Partial<CloudAccount>): Promise<CloudAccount> => {
    return _unwrap(await fetchClient.patch<CloudAccount>(`/api/v1/cloud-accounts/${id}`, data));
  },

  delete: async (id: string): Promise<void> => {
    _unwrap(await fetchClient.delete<unknown>(`/api/v1/cloud-accounts/${id}`));
  },

  testConnection: async (id: string): Promise<{ success: boolean; message: string }> => {
    return _unwrap(await fetchClient.post<{ success: boolean; message: string }>(`/api/v1/cloud-accounts/${id}/test`));
  },

  triggerSync: async (id: string): Promise<DiscoveryJob> => {
    return _unwrap(await fetchClient.post<DiscoveryJob>(`/api/v1/cloud-accounts/${id}/sync`));
  },

  getRegions: async (id: string): Promise<string[]> => {
    return _unwrap(await fetchClient.get<string[]>(`/api/v1/cloud-accounts/${id}/regions`));
  },
};


// ============================================================================
// Connectors freshness API (cloud-connector service)
// ============================================================================
// Wiz-parity (2026-05-16): replaces the "Scanning... 10%" polling UX with an
// always-current "Last activity X min ago" indicator. The backend endpoint
// (services/core/cloud-connector/app/routers/connector_lifecycle.py) combines
// last_sync_at + last_event_at (CloudTrail/Activity/Audit ingest) + last
// inventory mutation into a single payload with computed age fields.

export interface ConnectorFreshness {
  connector_id: string;
  name: string | null;
  provider: string;
  account_id: string;
  status: string | null;
  scanning_paused?: boolean;
  resource_count?: number | string | null;
  finding_count?: number | string | null;
  last_sync_at: string | null;
  last_sync_status?: string | null;
  last_event_at: string | null;
  last_resource_at: string | null;
  sync_age_seconds: number | null;
  event_age_seconds: number | null;
  resource_age_seconds: number | null;
  freshest_age_seconds: number | null;
  events_24h?: number;
  generated_at: string;
}

export interface ConnectorFreshnessList {
  items: ConnectorFreshness[];
  total: number;
  tenant_id: string;
  generated_at: string;
  warning?: string;
  error?: string;
}

// ── Provider depth disclosure ────────────────────────────────────────────────
// GET /api/v1/connectors/providers carries a `coverage` block per provider,
// measured per request-cycle by shared/common/provider_coverage.py (never a
// hand-maintained copy — the last one, cspm-scan-service's /capabilities, went
// stale without anyone noticing). Until this reached the UI the backend knew
// AWS carries 2,174 rules and Linode cannot produce a finding at all, and every
// tile rendered identically.

/** The four honest states a provider can be in. Not a free-form string. */
export type ProviderCoverageState =
  | 'assessed'            // a rule corpus evaluates this provider
  | 'inventory_only'      // resources are enumerated; no rules exist to judge them
  | 'unwired'             // collector modules exist but nothing imports them
  | 'external_collector'; // collection lives outside the shared fleet

export interface ProviderCoverage {
  /** null means "not a collector fleet" (e.g. the k8s API is one endpoint), NOT zero. */
  collectors: number | null;
  rules_total: number;
  rules_by_pillar: Record<string, number>;
  /** 0..1 against the deepest provider (AWS today). Not a quality score — a depth ratio. */
  depth_relative_to_deepest: number;
  coverage: ProviderCoverageState;
  /** Why this provider sits where it does. Rendered verbatim; never summarised away. */
  note: string;
}

export interface AdvertisedProvider {
  id: string;
  name: string;
  icon?: string;
  supported_auth: string[];
  support: string;
  creatable: boolean;
  credential_validation: 'real' | 'none';
  requires_external_id?: boolean;
  external_id_hint?: string;
  unsupported_reason?: string;
  /**
   * UI-facing tile ids that onboard THROUGH this provider — `eks`/`aks`/`gke`/
   * `k8s` for `kubernetes`, `ecr`/`acr`/`gcr`/`dockerhub` for
   * `container_registry`, `github`/`gitlab` for `source_repository`.
   *
   * Sent by the backend so this app never keeps an alias table of its own. The
   * Settings tile grid carried 25 ids against a 13-id catalog until 2026-08-09
   * and 16 of them completed the add-connector wizard without ever calling the
   * backend, leaving a "pending" row the next poll silently deleted. The
   * mapping has to come from the same file as the credential probe, or it goes
   * stale exactly the way that grid did.
   */
  variants?: string[];
  /** Absent when the backend could not measure depth for this provider. */
  coverage?: ProviderCoverage;
}

/**
 * `providers: null` means WE COULD NOT LOOK; `[]` would mean we looked and the
 * catalog is empty. Collapsing the two is the defect this repo has paid for
 * repeatedly, and the gateway makes it easy to hit here: FALLBACK_RESPONSES is
 * matched with `path.startswith(prefix)`, so when cloud-connector (8001) is
 * down, /api/v1/connectors/providers matches the "/api/v1/connectors" stub and
 * returns `{connectors: [], total: 0}` with HTTP 200 — a payload carrying no
 * `providers` key at all. Rendering that as "no coverage" would report an
 * outage as a measurement.
 */
export interface AdvertisedProviderList {
  providers: AdvertisedProvider[] | null;
  /** Set when the answer came from a gateway fallback stub, with the reason. */
  unavailableReason: string | null;
}

export const connectorsApi = {
  /** Aggregate freshness across all connectors in the current tenant. */
  listFreshness: async (params?: { provider?: string }): Promise<ConnectorFreshnessList> => {
    const res = await fetchClient.get<ConnectorFreshnessList>('/api/v1/connectors/freshness', params as any);
    return _unwrap(res);
  },

  /** Per-connector freshness — accepts the UUID or the provider-native account_id. */
  getFreshness: async (connectorId: string): Promise<ConnectorFreshness> => {
    return _unwrap(
      await fetchClient.get<ConnectorFreshness>(`/api/v1/connectors/${connectorId}/freshness`)
    );
  },

  /**
   * The advertised provider catalog with measured depth per provider.
   *
   * Deliberately NOT `_unwrap`ped: _unwrap returns `data` and discards the
   * `isFallback` flag the client lifts off the X-Fallback header, and that flag
   * is the only thing separating "the connector service is down" from "this
   * platform supports nothing".
   */
  listProviders: async (): Promise<AdvertisedProviderList> => {
    const res = await fetchClient.get<{ providers?: AdvertisedProvider[] }>(
      '/api/v1/connectors/providers'
    );
    if (res.error) {
      return { providers: null, unavailableReason: res.error };
    }
    if (res.isFallback) {
      return {
        providers: null,
        unavailableReason: `connector service unreachable (${res.fallbackReason || 'fallback'})`,
      };
    }
    const list = res.data?.providers;
    if (!Array.isArray(list)) {
      // 200 with the wrong shape — an untagged stub or a contract change.
      // Either way we did not measure anything.
      return { providers: null, unavailableReason: 'unrecognised provider catalog response' };
    }
    return { providers: list, unavailableReason: null };
  },
};


// ============================================================================
// Admin Webhook Secrets API (Round 13/14)
// ============================================================================
// Per-tenant inbound CDR webhook secrets. Backend: admin-service router
// services/platform/admin-service/app/routers/webhook_secrets.py.
// Plaintext returned ONCE on create/rotate — admin UI must capture it
// immediately, the backend never re-serves it.

export interface WebhookSecret {
  id: string;
  tenant_id: string;
  /** "aws" | "azure" | "gcp" | "" (tenant-wide fallback) */
  provider: string;
  name: string;
  /** First 8 chars of the plaintext, for UI identification. */
  secret_prefix: string;
  /** "wsk_abc1…********" form — safe to display. */
  secret_masked?: string;
  /** SNS Topic ARNs (AWS) or service-account emails (GCP) — empty array for plain HMAC. */
  source_allowlist: string[];
  status: 'active' | 'revoked';
  created_by?: string | null;
  created_at: string;
  updated_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  revoked_by?: string | null;
}

export interface WebhookSecretCreateResponse extends WebhookSecret {
  /** Plaintext shown ONCE — never returned by list/get endpoints. */
  secret: string;
  warning?: string;
}

export interface WebhookSecretRotateResponse {
  old_id: string;
  new_id: string;
  tenant_id: string;
  secret: string;
  secret_prefix: string;
  status: 'active';
  created_at: string;
  warning?: string;
}

export interface WebhookSecretListResponse {
  items: WebhookSecret[];
  total: number;
  tenant_id: string;
  warning?: string;
  error?: string;
}

export const adminWebhookSecretsApi = {
  list: async (): Promise<WebhookSecretListResponse> => {
    return _unwrap(await fetchClient.get<WebhookSecretListResponse>('/api/v1/admin/webhook-secrets'));
  },

  get: async (id: string): Promise<WebhookSecret> => {
    return _unwrap(await fetchClient.get<WebhookSecret>(`/api/v1/admin/webhook-secrets/${encodeURIComponent(id)}`));
  },

  create: async (body: {
    name: string;
    provider?: '' | 'aws' | 'azure' | 'gcp';
    source_allowlist?: string[];
  }): Promise<WebhookSecretCreateResponse> => {
    return _unwrap(await fetchClient.post<WebhookSecretCreateResponse>(
      '/api/v1/admin/webhook-secrets',
      body,
    ));
  },

  revoke: async (id: string): Promise<{ id: string; status: 'revoked' }> => {
    return _unwrap(await fetchClient.post<{ id: string; status: 'revoked' }>(
      `/api/v1/admin/webhook-secrets/${encodeURIComponent(id)}/revoke`,
    ));
  },

  rotate: async (id: string): Promise<WebhookSecretRotateResponse> => {
    return _unwrap(await fetchClient.post<WebhookSecretRotateResponse>(
      `/api/v1/admin/webhook-secrets/${encodeURIComponent(id)}/rotate`,
    ));
  },

  /** Send a canonical signed sample event through the CDR ingest endpoint
   * to verify the full chain (decrypt → sign → rate-limit → HMAC verify →
   * envelope parse → ingestion). Backend route added in Round 15. */
  testDelivery: async (id: string): Promise<WebhookSecretTestDeliveryResponse> => {
    return _unwrap(await fetchClient.post<WebhookSecretTestDeliveryResponse>(
      `/api/v1/admin/webhook-secrets/${encodeURIComponent(id)}/test-delivery`,
    ));
  },
};


export interface WebhookSecretTestDeliveryResponse {
  /** True iff CDR returned 2xx (full chain accepted). */
  delivered: boolean;
  /** HTTP status code CDR returned. 0 when CDR was unreachable. */
  status_code: number;
  /** First 2000 chars of CDR's response body. */
  response: string;
  duration_ms: number;
  test_event_id: string;
  /** True iff status_code is NOT 401 — distinguishes "bad sig" from "rate-limited". */
  verification_passed: boolean;
  cdr_url: string;
  provider: string;
  secret_name: string;
  error?: string | null;
  message: string;
}


// ============================================================================
// Webhook Delivery Stats (Round 16 — "Recent deliveries" panel)
// ============================================================================
// Sources the per-tenant in-memory counter maintained by
// services/domain/cdr/services/cloud-log-analytics/app/main.py. Surfaces three
// outcomes per route over a sliding 60-min window so admins can spot a
// misconfigured SNS topic / Event Grid subscription / Pub/Sub push before it
// becomes silent data loss.

export interface WebhookDeliveryPoint {
  /** Unix-epoch seconds, minute-aligned. */
  bucket: number;
  accepted: number;
  sig_failed: number;
  rate_limited: number;
}

export interface WebhookDeliveryRouteSummary {
  accepted: number;
  sig_failed: number;
  rate_limited: number;
  total: number;
  sparkline: WebhookDeliveryPoint[];
}

export interface WebhookDeliverySummary {
  tenant_id: string;
  window_minutes: number;
  /** Unix-epoch seconds for the most-recent minute bucket. */
  generated_at: number;
  totals: {
    accepted: number;
    sig_failed: number;
    rate_limited: number;
    total: number;
  };
  /** Empty object when this tenant has had no traffic in the window. */
  by_route: Record<string, WebhookDeliveryRouteSummary>;
}

export const webhookDeliveriesApi = {
  /** Per-tenant rollup of webhook delivery outcomes. Tenant is taken from
   * the request's `X-Tenant-ID-Verified` / `X-Tenant-ID` header (injected
   * by `fetchClient`); window is clamped server-side to ≤60 minutes. */
  summary: async (windowMinutes: number = 60): Promise<WebhookDeliverySummary> => {
    return _unwrap(await fetchClient.get<WebhookDeliverySummary>(
      '/api/v1/cdr/webhooks/delivery-stats',
      { window_minutes: windowMinutes },
    ));
  },
};


// ============================================================================
// Resources API (Asset Inventory Service)
// ============================================================================

export const resourcesApi = {
  list: async (params?: {
    account_id?: string;
    provider?: CloudProvider;
    resource_type?: string;
    category?: string;
    region?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CloudResource>> => {
    return _unwrap(await fetchClient.get('/api/v1/assets', params));
  },

  get: async (id: string): Promise<CloudResource> => {
    return _unwrap(await fetchClient.get<CloudResource>(`/api/v1/assets/${id}`));
  },

  getTypes: async (provider?: CloudProvider): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/assets/types', { provider } as any));
  },

  getCategories: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/assets/categories'));
  },

  getSummary: async (accountId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/assets/summary', { account_id: accountId }));
  },
};

// ============================================================================
// Remediation Service API
// ============================================================================

export const remediationServiceApi = {
  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/dashboard'));
  },
  getActions: async (params?: { status?: string; provider?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/actions', params));
  },
  getAction: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/actions/${id}`));
  },
  createAction: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/actions', data));
  },
  executeAction: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/actions/${id}/execute`));
  },
  rollbackAction: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/actions/${id}/rollback`));
  },
  getTemplates: async (params?: { provider?: string; category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/templates', params));
  },
  getTemplate: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/templates/${id}`));
  },
  applyTemplate: async (id: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/templates/${id}/apply`, data));
  },
  getRiskPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/risk-posture'));
  },
  getCampaigns: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/campaigns', params));
  },
  getCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/campaigns/${id}`));
  },
  createCampaign: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/campaigns', data));
  },
  executeCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/campaigns/${id}/execute`));
  },
  cancelCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/campaigns/${id}/cancel`));
  },
  getPlaybooks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/playbooks'));
  },
  getPlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/playbooks/${id}`));
  },
  createPlaybook: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/playbooks', data));
  },
  executePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/playbooks/${id}/execute`));
  },
  togglePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/playbooks/${id}/toggle`));
  },
  deletePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/remediation/playbooks/${id}`));
  },
  getSchedules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/schedules'));
  },
  createSchedule: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/schedules', data));
  },
  updateSchedule: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/remediation/schedules/${id}`, data));
  },
  deleteSchedule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/remediation/schedules/${id}`));
  },
  getIntegrations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/integrations'));
  },
  createIntegration: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/integrations', data));
  },
  updateIntegration: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/remediation/integrations/${id}`, data));
  },
  deleteIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/remediation/integrations/${id}`));
  },
  testIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/integrations/${id}/test`));
  },
  getAuditLog: async (params?: { limit?: number; offset?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/audit-log', params));
  },
  getComplianceReport: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/compliance-report'));
  },

  // Workflow Impact Preview — the real preview handler is registered at
  // POST /api/v1/workflows/{id}/preview (remediation-service workflows.py).
  // The prior /api/v1/remediation/workflows/{id}/preview path had no handler
  // (only checkpoint/rollback live under that prefix) so preview always 404'd.
  previewImpact: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/preview`));
  },

  // --- IaC Fix Generation ---
  getIacFix: async (findingId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/${findingId}/iac-fix`));
  },
  getIacFixAll: async (findingId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/${findingId}/iac-fix-all`));
  },
  listIacFixTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/iac-fixes'));
  },
  generateCustomFix: async (data: { finding_id: string; fix_type?: string; provider?: string; parameters?: Record<string, any> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/generate-fix', data));
  },
  getFixTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/generate-fix/templates'));
  },

  // --- Workflow Management ---
  getWorkflows: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows', params));
  },
  getWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}`));
  },
  getWorkflowTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/templates'));
  },
  getWorkflowTemplate: async (templateId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/templates/${templateId}`));
  },
  createWorkflowFromTemplate: async (data: { template_id: string; name?: string; parameters?: Record<string, any> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/workflows/from-template', data));
  },
  instantiateTemplate: async (data: { template_id: string; parameters?: Record<string, any> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/workflows/templates/instantiate', data));
  },
  createWorkflow: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/workflows', data));
  },
  executeWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/execute`));
  },
  executeWorkflowStep: async (workflowId: string, stepIndex: number): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/execute-step/${stepIndex}`));
  },
  rollbackWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/rollback`));
  },
  cancelWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/cancel`));
  },
  validateWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}/validate`));
  },
  requestWorkflowApproval: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/request-approval`));
  },
  approveWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/approve`));
  },
  rejectWorkflow: async (workflowId: string, data?: { reason?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/reject`, data));
  },
  getWorkflowExecutions: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/executions', params));
  },
  getWorkflowExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/executions/${executionId}`));
  },
  cancelWorkflowExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/executions/${executionId}/cancel`));
  },
  getWorkflowHistory: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}/executions`));
  },
  getPendingApprovals: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/approvals/pending'));
  },
  respondToApproval: async (approvalId: string, data: { action: string; comment?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/approvals/${approvalId}/respond`, data));
  },
  getWorkflowActions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/actions'));
  },
  triggerWorkflowWebhook: async (workflowId: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/webhooks/${workflowId}`, data));
  },

  // --- IaC Fix Templates ---
  getIacTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/iac-templates'));
  },
  getIacTemplateByType: async (findingType: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/iac-templates/${findingType}`));
  },
  getActionTemplate: async (actionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/actions/${actionId}/template`));
  },

  // --- Cost & Effort Estimates ---
  getCostEstimates: async (params?: { severity?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/cost-estimate', params));
  },
  calculateCostEstimate: async (data: { action_id?: string; finding_id?: string; remediation_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/cost-estimate', data));
  },
  getEffortEstimates: async (params?: { severity?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/effort-estimate', params));
  },
  calculateEffortEstimate: async (data: { action_id?: string; finding_id?: string; remediation_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/effort-estimate', data));
  },

  // ========================================================================
  // Custom Remediation Scripts
  // ========================================================================

  getCustomScripts: async (params?: { language?: string; target?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/custom-scripts', params));
  },

  getCustomScript: async (scriptId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/custom-scripts/${scriptId}`));
  },

  createCustomScript: async (data: { name: string; language: string; target: string; code: string; description?: string; parameters?: any[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/custom-scripts', data));
  },

  executeCustomScript: async (scriptId: string, params?: { parameters?: Record<string, any>; dry_run?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/custom-scripts/${scriptId}/execute`, params));
  },

  updateCustomScript: async (scriptId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/remediation/custom-scripts/${scriptId}`, data));
  },

  deleteCustomScript: async (scriptId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/remediation/custom-scripts/${scriptId}`));
  },

  getScriptExecutions: async (scriptId: string, params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/custom-scripts/${scriptId}/executions`, params));
  },

  // ========================================================================
  // AI Remediation Playbooks
  // ========================================================================

  getAIPlaybooks: async (params?: { finding_type?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/ai-playbooks', params));
  },

  createAIPlaybook: async (data: { finding_type: string; name?: string; category?: string; description?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/ai-playbooks', data));
  },

  getAIPlaybook: async (playbookId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/ai-playbooks/${playbookId}`));
  },

  updateAIPlaybook: async (playbookId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/remediation/ai-playbooks/${playbookId}`, data));
  },

  deleteAIPlaybook: async (playbookId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/remediation/ai-playbooks/${playbookId}`));
  },

  // --- Impact Analysis ---
  getActionImpact: async (actionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/actions/${actionId}/impact`));
  },

  // --- Verify Fix ---
  verifyFix: async (actionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/actions/${actionId}/verify`));
  },

  // --- SLA Status ---
  getSLAStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/sla/status'));
  },

  // --- SLA Escalation ---
  escalateSLA: async (data: { finding_ids?: string[]; reason?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/sla/escalate', data));
  },

  // --- Auto-Generate Template ---
  generateTemplate: async (data: { finding_type?: string; provider?: string; resource_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/templates/generate', data));
  },

  // --- Cross-Account Execute ---
  crossAccountExecute: async (data: { action_id: string; account_ids: string[]; dry_run?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/cross-account/execute', data));
  },

  // Fix-in-code
  createFixInCode: (data: { finding_id: string; repo_url: string; branch?: string; github_token: string; iac_format?: string }) =>
    fetchClient.post('/api/v1/remediation/fix-in-code', data).then(_thenUnwrap),
  getFixInCodePRs: () =>
    fetchClient.get('/api/v1/remediation/fix-in-code/prs').then(_thenUnwrap),
  // Ticket creation
  createRemediationTicket: (data: { finding_id: string; integration_id: string; priority_override?: string }) =>
    fetchClient.post('/api/v1/remediation/tickets/create', data).then(_thenUnwrap),
  getRemediationTickets: (params?: { status?: string }) =>
    fetchClient.get('/api/v1/remediation/tickets', params).then(_thenUnwrap),
  syncTicket: (ticketId: string) =>
    fetchClient.post(`/api/v1/remediation/tickets/${ticketId}/sync`).then(_thenUnwrap),
  // Live SLA
  getLiveSLA: () =>
    fetchClient.get('/api/v1/remediation/sla/live').then(_thenUnwrap),
  // Re-scan
  triggerRescan: (actionId: string) =>
    fetchClient.post(`/api/v1/remediation/actions/${actionId}/rescan`).then(_thenUnwrap),
  getRescanResults: () =>
    fetchClient.get('/api/v1/remediation/rescans').then(_thenUnwrap),
  // Prioritized remediation
  getPrioritizedRemediation: () =>
    fetchClient.get('/api/v1/remediation/prioritized').then(_thenUnwrap),
  createPR: async (data: { vuln_id?: string; cve_id?: string; fix_code: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/pull-requests', data));
  },

  // ============================================================================
  // Stage 21 R24 — Remediation Validation (mig 633)
  // ============================================================================
  verifyTask: async (taskId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/remediation/verify/${taskId}`));
  },
  getValidationRun: async (runId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/remediation/validation-runs/${runId}`));
  },
  listValidationRuns: async (params?: {
    tenant_id?: string;
    status?: string;
    resource_id?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/validation-runs', params as any));
  },
};

// ============================================================================
// Notification Service API
// ============================================================================

export const notificationServiceApi = {
  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/dashboard'));
  },
  getChannels: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/channels'));
  },
  getChannel: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/channels/${id}`));
  },
  createChannel: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/channels', data));
  },
  updateChannel: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/notifications/channels/${id}`, data));
  },
  deleteChannel: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/channels/${id}`));
  },
  testChannel: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/channels/${id}/test`));
  },
  getRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/rules'));
  },
  getRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/rules/${id}`));
  },
  createRule: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/rules', data));
  },
  updateRule: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/notifications/rules/${id}`, data));
  },
  deleteRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/rules/${id}`));
  },
  toggleRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/rules/${id}/toggle`));
  },
  getHistory: async (params?: { status?: string; channel?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/history', params as any));
  },
  getNotification: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/history/${id}`));
  },
  retryNotification: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/history/${id}/retry`));
  },
  getTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/templates'));
  },
  createTemplate: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/templates', data));
  },
  updateTemplate: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/notifications/templates/${id}`, data));
  },
  deleteTemplate: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/templates/${id}`));
  },
  getDigest: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/digest'));
  },
  getPreferences: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/preferences'));
  },
  updatePreferences: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>('/api/v1/notifications/preferences', data));
  },
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/statistics'));
  },
  testSend: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/test-send', data));
  },
  getEscalationPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/escalation-policies'));
  },
  createEscalationPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/escalation-policies', data));
  },
  getEscalationPolicy: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/escalation-policies/${id}`));
  },
  deleteEscalationPolicy: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/escalation-policies/${id}`));
  },

  // Retry History
  getRetryHistory: async (historyId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/history/${historyId}/retry-history`));
  },

  // Channel Health
  getChannelHealth: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/health/channels'));
  },

  // Bi-directional Sync
  syncJira: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/sync/jira', data || {}));
  },
  syncServicenow: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/sync/servicenow', data || {}));
  },

  // Notification Groups
  getGroups: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/groups'));
  },
  notifyGroup: async (groupId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/groups/${groupId}/notify`, data));
  },

  // Webhook Signing
  testWebhookSignature: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/webhooks/test-signature', data));
  },
  getWebhookSigningConfig: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/webhooks/signing-config'));
  },

  // Rate Limits
  getRateLimits: async (tenantId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(
      '/api/v1/notifications/rate-limits',
      tenantId ? { tenant_id: tenantId } : undefined,
    ));
  },

  updateRateLimits: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>('/api/v1/notifications/rate-limits', data));
  },

  // Delivery Latency Metrics
  getDeliveryLatency: async (channel?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(
      '/api/v1/notifications/metrics/latency',
      channel ? { channel } : undefined,
    ));
  },

  // Dead Letter Queue
  getDeadLetterQueue: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/dead-letter-queue'));
  },
  getDeadLetterItem: async (itemId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/dead-letter-queue/${itemId}`));
  },
  reprocessDeadLetterItem: async (itemId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/dead-letter-queue/${itemId}/reprocess`));
  },
  deleteDeadLetterItem: async (itemId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/dead-letter-queue/${itemId}`));
  },

  // Circuit Breakers
  getCircuitBreakers: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/circuit-breakers'));
  },
  getChannelCircuitBreaker: async (channelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/notifications/channels/${channelId}/circuit-breaker`));
  },
  resetCircuitBreaker: async (channelId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/notifications/channels/${channelId}/circuit-breaker/reset`));
  },

  // Suppression Windows
  listSuppressionWindows: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/notifications/suppression-windows'));
  },
  createSuppressionWindow: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/notifications/suppression-windows', data));
  },
  deleteSuppressionWindow: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/notifications/suppression-windows/${id}`));
  },
};

// ============================================================================
// Dashboard API (Aggregated)
// ============================================================================

// Migrated to canonical fetch client (services/api/client.ts).
// Public signatures unchanged: throws on non-2xx, returns unwrapped data.
export const dashboardApi = {
  getSummary: async (): Promise<DashboardStats> => {
    return _unwrap(await fetchClient.get<DashboardStats>('/api/v1/dashboard/summary'));
  },

  getRiskTrends: async (days?: number): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dashboard/risk-trends', { days }));
  },

  getByProvider: async (): Promise<Record<CloudProvider, any>> => {
    return _unwrap(await fetchClient.get<Record<CloudProvider, any>>('/api/v1/dashboard/by-provider'));
  },

  getTopRisks: async (limit?: number): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dashboard/top-risks', { limit }));
  },

  getRecentActivity: async (limit?: number): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dashboard/recent-activity', { limit }));
  },

  getServicesHealth: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/services/health'));
  },

  getModulesHealth: async (module: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/services/health/${module}`));
  },
};

// ============================================================================
// Inventory API (CMDB & Asset Management)
// ============================================================================

// R164-B (2026-05-19): G-RT-17 — RuntimeContextEnvelope mirrors the R164-A
// GET /api/v1/runtime-context/{resource_id} response shape. All sub-sections
// are optional / nullable so the FE can render only the slices that have data.
export interface RuntimeContextEnvelope {
  asset?: {
    state?: string | null;
    last_observed_at?: string | null;
    is_internet_facing?: boolean;
    is_crown_jewel?: boolean;
    confidence?: number | string;
    runtime_source?: string;
  };
  pod_runtime?: {
    pod_phase?: string;
    ready?: boolean;
    restart_count_total?: number;
    node_name?: string;
    started_at?: string;
    container_states?: Array<{
      name: string;
      image?: string;
      state?: string;
      ready?: boolean;
      restart_count?: number;
    }>;
    host_network?: boolean;
    host_pid?: boolean;
    privileged_count?: number;
  } | null;
  identity_runtime?: {
    last_activity_at?: string;
    last_activity_days?: number;
    dormancy?: 'active' | 'dormant' | 'stale' | string;
    is_admin?: boolean;
  } | null;
  traffic_24h?: {
    inbound_bytes?: number;
    inbound_flow_count?: number;
    outbound_bytes?: number;
    outbound_flow_count?: number;
    top_inbound_peers?: Array<{ peer?: string; port?: number; protocol?: string; bytes?: number }>;
    top_outbound_peers?: Array<{ peer?: string; port?: number; protocol?: string; bytes?: number }>;
  } | null;
  runtime_evidence_findings?: Array<{
    finding_id?: string;
    issue_id?: string;
    rule_id?: string;
    severity?: string;
    observed_at?: string;
    evidence?: Record<string, unknown>;
  }>;
  image_runtime?: {
    pod_count?: number;
    cluster_count?: number;
    clusters?: string[];
  } | null;
  risk_signals?: Record<string, unknown> | null;
  freshness?: 'fresh' | 'stale' | 'missing';
  fetched_at?: string;
}

// R170-D (2026-05-19): G-TI-13/14 — ThreatContextEnvelope mirrors the R170-C
// GET /api/v1/threat-context/{resource_id} response shape. All sub-sections
// are optional / nullable so the FE can render only the slices that have data
// (mirrors the R164-B RuntimeContextEnvelope convention).
export interface ThreatContextEnvelope {
  resource_id?: string;
  asset?: {
    id?: string;
    name?: string;
    type?: string;
    provider?: string;
  };
  vulnerabilities?: Array<{
    cve_id: string;
    cvss_score?: number;
    is_in_kev?: boolean;
    kev_date_added?: string;
    kev_known_ransomware?: string;
    epss_score?: number;
    epss_percentile?: number;
    exploit_available?: boolean;
    exploit_sources?: string[];
    mitre_techniques?: string[];
    first_seen_at?: string;
    last_seen_at?: string;
  }>;
  iocs_observed?: Array<{
    ioc_type?: string;
    value?: string;
    source?: string;
    confidence?: number;
    first_seen_at?: string;
    last_seen_at?: string;
    edge_type?: string;
    tags?: string[];
  }>;
  mitre_attack?: {
    techniques?: Array<{ id: string; name?: string; tactic?: string; source?: string }>;
    tactics?: string[];
  };
  threat_actor_associations?: Array<{
    name: string;
    aliases?: string[];
    confidence?: number;
  }>;
  ransomware_associations?: Array<{
    family: string;
    evidence?: string;
  }>;
  malware_findings?: Array<{
    name: string;
    severity?: string;
    image?: string;
  }>;
  leaked_credentials?: Array<{
    source: string;
    first_seen_at?: string;
  }>;
  summary?: {
    kev_count?: number;
    high_epss_count?: number;
    exploit_available_count?: number;
    ioc_match_count?: number;
    mitre_technique_count?: number;
    overall_threat_score?: number;
  };
  freshness?: 'fresh' | 'stale' | 'missing';
  fetched_at?: string;
}

export const inventoryApi = {
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/stats'));
  },
  getResources: async (params?: {
    category?: string;
    provider?: string;
    region?: string;
    risk_level?: string;
    search?: string;
    limit?: number;
    offset?: number;
    // R164-B (2026-05-19): G-RT-17 — state filter param forwarded to
    // /api/v1/inventory/resources?state=<value> (R163-A query param) so the
    // dashboard State chip strip can request server-side filtered pages.
    state?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/resources', params));
  },
  getResource: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/inventory/resources/${id}`));
  },
  getDependencies: async (params?: {
    resource_id?: string;
    relationship_type?: string;
    criticality?: string;
    depth?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/dependencies', params));
  },
  getDependencyGraph: async (resourceId: string, depth?: number): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/inventory/dependencies/${resourceId}/graph`, { depth }));
  },
  getCostBreakdown: async (params?: {
    provider?: string;
    category?: string;
    region?: string;
    period?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/cost/breakdown', params));
  },
  getCostTrends: async (period?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/cost/trends', { period }));
  },
  getCostOptimization: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/cost/optimization'));
  },
  getTagGovernance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/tags/governance'));
  },
  getNonCompliantResources: async (params?: { tag?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/tags/non-compliant', params));
  },
  getTagCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/tags/coverage'));
  },
  updateResourceTags: async (resourceId: string, tags: Record<string, string>): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/inventory/resources/${resourceId}/tags`, { tags }));
  },
  getChangeHistory: async (params?: {
    resource_id?: string;
    change_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/changes', params));
  },
  getChangeTimeline: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/inventory/changes/${resourceId}/timeline`));
  },
  getLifecycleRecommendations: async (params?: {
    recommendation_type?: string;
    min_savings?: number;
    risk_level?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/lifecycle/recommendations', params));
  },
  executeLifecycleAction: async (recommendationId: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/inventory/lifecycle/recommendations/${recommendationId}/execute`, data));
  },
  dismissRecommendation: async (recommendationId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/inventory/lifecycle/recommendations/${recommendationId}/dismiss`));
  },
  getResourcesByCategory: async (category: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/inventory/resources/category/${category}`));
  },
  getResourcesByProvider: async (provider: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/inventory/resources/provider/${provider}`));
  },
  getOrphanedResources: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/resources/orphaned'));
  },
  getUntaggedResources: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/resources/untagged'));
  },
  getHighRiskResources: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/inventory/resources/high-risk'));
  },

  // R164-B (2026-05-19): G-RT-17 — unified runtime context envelope. Wraps
  // the R164-A GET /api/v1/runtime-context/{resource_id} endpoint that fuses
  // csp_inventory.resources.state + csp_kspm.pods + csp_graph runtime edges
  // + csp_graph.nodes.risk_signals + csp_core.findings.runtime_evidence into
  // a single payload for the FE Runtime tab + IssueDetail evidence panel.
  getRuntimeContext: async (resourceId: string): Promise<RuntimeContextEnvelope> => {
    return _unwrap(await fetchClient.get<RuntimeContextEnvelope>(
      `/api/v1/runtime-context/${encodeURIComponent(resourceId)}`
    ));
  },

  // R170-D (2026-05-19): G-TI-13/14 — unified threat-context envelope. Wraps
  // the R170-C GET /api/v1/threat-context/{resource_id} endpoint that fuses
  // CISA KEV / EPSS / exploit catalog / IOC feeds / MITRE ATT&CK technique
  // mappings / ransomware associations / malware findings / leaked
  // credentials into a single payload for the FE Threat tab +
  // IssueDetail ThreatEvidencePanel.
  getThreatContext: async (resourceId: string): Promise<ThreatContextEnvelope> => {
    return _unwrap(await fetchClient.get<ThreatContextEnvelope>(
      `/api/v1/threat-context/${encodeURIComponent(resourceId)}`
    ));
  },

  // SCIM APIs
  getSCIMServiceProviderConfig: async (tenantId: string): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/scim/v2/ServiceProviderConfig', {
      method: 'GET',
      params: { tenant_id: tenantId },
    }));
  },
  getSCIMResourceTypes: async (tenantId: string): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/scim/v2/ResourceTypes', {
      method: 'GET',
      params: { tenant_id: tenantId },
    }));
  },
  createSCIMUser: async (tenantId: string, user: any, bearerToken: string): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/scim/v2/Users', {
      method: 'POST',
      body: user,
      params: { tenant_id: tenantId },
      headers: { Authorization: `Bearer ${bearerToken}` },
    }));
  },
  getSCIMUser: async (tenantId: string, userId: string, bearerToken: string): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/scim/v2/Users/${userId}`, {
      method: 'GET',
      params: { tenant_id: tenantId },
      headers: { Authorization: `Bearer ${bearerToken}` },
    }));
  },
  listSCIMUsers: async (
    tenantId: string,
    bearerToken: string,
    params?: { startIndex?: number; count?: number; filter?: string }
  ): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>('/scim/v2/Users', {
      method: 'GET',
      params: { tenant_id: tenantId, ...params },
      headers: { Authorization: `Bearer ${bearerToken}` },
    }));
  },
  updateSCIMUser: async (
    tenantId: string,
    userId: string,
    user: any,
    bearerToken: string
  ): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/scim/v2/Users/${userId}`, {
      method: 'PUT',
      body: user,
      params: { tenant_id: tenantId },
      headers: { Authorization: `Bearer ${bearerToken}` },
    }));
  },
  deleteSCIMUser: async (tenantId: string, userId: string, bearerToken: string): Promise<any> => {
    return _unwrapWithOpts(fetchClient.request<any>(`/scim/v2/Users/${userId}`, {
      method: 'DELETE',
      params: { tenant_id: tenantId },
      headers: { Authorization: `Bearer ${bearerToken}` },
    }));
  },
};

// ============================================================================
// RBAC API (Advanced Role-Based Access Control - Phase 9)
// ============================================================================

export const rbacApi = {
  // Custom Roles
  listRoles: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/rbac/roles'));
  },
  createRole: async (role: { name: string; description: string; permissions: string[]; inherits_from?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/rbac/roles', role));
  },
  getRole: async (roleId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/rbac/roles/${roleId}`));
  },
  updateRole: async (roleId: string, updates: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/rbac/roles/${roleId}`, updates));
  },
  deleteRole: async (roleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/rbac/roles/${roleId}`));
  },
  listPermissions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/rbac/permissions'));
  },
  getRoleHierarchy: async (roleId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/rbac/roles/${roleId}/hierarchy`));
  },
  createRoleFromTemplate: async (templateName: string, customizations?: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/rbac/roles/template', { template_name: templateName, ...customizations }));
  },

  // API Keys
  createApiKey: async (data: { name: string; expires_in_days?: number; permissions?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/rbac/api-keys', data));
  },
  listApiKeys: async (): Promise<any> => {
    // The real keys live in csp_admin.api_keys served by /api/v1/admin/api-keys
    // (returns { items, total }); the rbac endpoint is an empty twin.
    return _unwrap(await fetchClient.get('/api/v1/admin/api-keys'));
  },
  rotateApiKey: async (keyId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/rbac/api-keys/${keyId}/rotate`));
  },
  revokeApiKey: async (keyId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/rbac/api-keys/${keyId}`));
  },
  getApiKeyUsage: async (keyId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/rbac/api-keys/${keyId}/usage`));
  },

  // Audit Logs
  queryAuditLogs: async (query: {
    user_id?: string;
    action?: string;
    resource_type?: string;
    start_time?: string;
    end_time?: string;
    limit?: number;
  }): Promise<any> => {
    // The rbac POST endpoint 405s; the real audit trail is GET /api/v1/admin/audit-log
    // which returns { events, total, ... }. Map events → logs for callers.
    const params: Record<string, any> = {};
    if (query.user_id) params.user_id = query.user_id;
    if (query.action) params.action_type = query.action;
    if (query.resource_type) params.resource_type = query.resource_type;
    if (query.start_time) params.date_from = query.start_time;
    if (query.end_time) params.date_to = query.end_time;
    if (query.limit) params.page_size = query.limit;
    const resp: any = _unwrap(await fetchClient.get('/api/v1/admin/audit-log', params));
    return { ...resp, logs: resp?.events ?? [] };
  },
  getUserActivity: async (userId: string, params?: { start_time?: string; end_time?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/rbac/audit/users/${userId}/activity`, params));
  },
};

// ============================================================================
// Analytics API (Advanced Analytics - Phase 10)
// ============================================================================

/**
 * One account's scan-loop health, as `csp_cspm.v_scan_health` computes it.
 *
 * READ `seconds_since_data`, NOT `seconds_since_last_success`. The latter
 * counts only `status='completed'` scans, and 31 of 57 terminal scans on this
 * deployment are `partial` — a DEGRADED SUCCESS that did collect data
 * (`_aggregate_terminal_status` assigns it precisely when data is present).
 * Measured 2026-08-12: that field read 41,822s (11.6h) while a data-bearing
 * scan had finished 26 minutes earlier. It is kept only so pre-existing
 * callers do not change meaning under them (mig 1269).
 */
export interface ScanHealthRow {
  account_id: string;
  total_scans: number;
  terminal_scans: number;
  completed_scans: number;
  partial_scans: number;
  running_scans: number;
  failed_scans: number;
  /** In-flight scans whose HEARTBEAT has been silent past the 90-min rule. */
  stuck_scans: number;
  last_successful_scan: string | null;
  seconds_since_last_success: number | null;
  /** Last terminal scan that collected something — `completed` OR `partial`. */
  last_scan_with_data: string | null;
  seconds_since_data: number | null;
  /** The account's OWN observed cadence (median gap, last 12 terminal scans). */
  expected_interval_seconds: number | null;
  avg_duration_seconds: number | null;
  /** `unknown` = too little history to judge. It is NOT `healthy`. */
  health_state: 'stuck' | 'stale' | 'lagging' | 'unknown' | 'healthy';
}

export const analyticsApi = {
  /**
   * Scan-loop health per account. Rows arrive worst-first (stuck, stale,
   * lagging, unknown, healthy).
   */
  getScanHealth: async (): Promise<{ items: ScanHealthRow[]; total: number }> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/system/scan-health'));
  },

  // Risk Scoring
  calculateRiskScore: async (data: {
    resource_id: string;
    resource_type: string;
    factors: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/risk/score', data));
  },
  batchScoreResources: async (resources: Array<{
    resource_id: string;
    resource_type: string;
    factors: Record<string, any>;
  }>): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/risk/batch', { resources }));
  },
  getRiskTrends: async (resourceId: string, params?: { days?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/risk/trends/${resourceId}`, params));
  },

  // Anomaly Detection
  detectAnomaly: async (data: {
    entity_id: string;
    entity_type: string;
    metric_name: string;
    value: number;
    metadata?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/anomalies/detect', data));
  },
  listAnomalies: async (params?: {
    entity_type?: string;
    severity?: string;
    start_time?: string;
    end_time?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/anomalies', params));
  },
  getAnomalySummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/anomalies/summary'));
  },

  // Predictive Analytics
  getForecast: async (seriesId: string, params?: { days_ahead?: number; method?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/forecast/${seriesId}`, params));
  },
  getForecastSummary: async (seriesId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/forecast/${seriesId}/summary`));
  },
  calculateAttackLikelihood: async (data: {
    asset_id: string;
    vulnerabilities: any[];
    attack_vectors?: any[];
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/attack-likelihood', data));
  },

  // Security Metrics
  getSecurityScore: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/security-score'));
  },
  calculateSecurityScore: async (data: {
    findings_data: Record<string, any>;
    compliance_data?: Record<string, any>;
    vulnerability_data?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/security-score/calculate', data));
  },
  getKPIs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/kpis'));
  },
  getExecutiveDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/dashboard/executive'));
  },

  // Query Engine
  executeQuery: async (query: {
    data_source: string;
    filters?: Record<string, any>;
    aggregations?: any[];
    sort_by?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/query', query));
  },

  // ML Training API methods
  getModels: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/ml/models'));
  },
  getModel: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/ml/models/${modelId}`));
  },
  deployModel: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/analytics/ml/models/${modelId}/deploy`));
  },
  retireModel: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/analytics/ml/models/${modelId}/retire`));
  },
  retrainModel: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/analytics/ml/models/${modelId}/retrain`));
  },
  getTrainingJobs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/ml/training/jobs'));
  },
  createTrainingJob: async (config: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/ml/training/jobs', config));
  },
  cancelTrainingJob: async (jobId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/analytics/ml/training/jobs/${jobId}/cancel`));
  },
  getDatasets: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/analytics/ml/datasets'));
  },
  getDatasetPreview: async (datasetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/ml/datasets/${datasetId}/preview`));
  },
  splitDataset: async (datasetId: string, ratios: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/analytics/ml/datasets/${datasetId}/split`, ratios));
  },
  getModelEvaluation: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/analytics/ml/models/${modelId}/evaluation`));
  },
  compareModels: async (modelIds: string[]): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/analytics/ml/models/compare', { model_ids: modelIds }));
  },
};

// ============================================================================
// Workflows API (Visual Workflow Automation - Phase 11)
// ============================================================================

export const workflowsApi = {
  // Workflows
  createWorkflow: async (workflow: {
    name: string;
    description: string;
    nodes: any[];
    triggers?: any[];
    variables?: any[];
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/workflows', workflow));
  },
  listWorkflows: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows', params));
  },
  getWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}`));
  },
  updateWorkflow: async (workflowId: string, updates: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/workflows/${workflowId}`, updates));
  },
  deleteWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/workflows/${workflowId}`));
  },

  // Execution
  executeWorkflow: async (workflowId: string, context?: Record<string, any>): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/execute`, { context }));
  },
  getExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/executions/${executionId}`));
  },
  listExecutions: async (workflowId: string, params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}/executions`, params));
  },
  listAllExecutions: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/executions', params));
  },
  cancelExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/executions/${executionId}/cancel`));
  },

  // Approvals — merged workflow + remediation queue (Wiz-parity)
  listPendingApprovals: async (): Promise<any> => {
    // Fetch both sources in parallel and return a merged list the UI can
    // display in a single queue. Each item carries a `source` flag so the
    // respond handler knows which backend to hit.
    const [wfRes, remRes] = await Promise.all([
      fetchClient.get<{ approvals?: any[] }>('/api/v1/workflows/approvals/pending'),
      fetchClient.get<{ approvals?: any[] }>('/api/v1/remediation/approvals'),
    ]);
    const wf = (wfRes.data?.approvals || []).map((a: any) => ({ ...a, _source: 'workflow' }));
    const rem = (remRes.data?.approvals || []).map((a: any) => ({
      ...a,
      id: a.action_id || a.id,
      title: a.name || a.title,
      _source: 'remediation',
    }));
    return { approvals: [...wf, ...rem], total: wf.length + rem.length };
  },
  respondToApproval: async (approvalId: string, approved: boolean, comments?: string, source?: string): Promise<any> => {
    // Route to the correct backend based on source.
    if (source === 'remediation') {
      const endpoint = approved
        ? `/api/v1/remediation/actions/${approvalId}/approve`
        : `/api/v1/remediation/actions/${approvalId}/reject`;
      return _unwrap(await fetchClient.post(endpoint, { comments, rejected_by: 'user' }));
    }
    return _unwrap(await fetchClient.post(`/api/v1/workflows/approvals/${approvalId}/respond`, { approved, comments }));
  },

  // Templates
  listTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/templates'));
  },
  getTemplate: async (templateId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/templates/${templateId}`));
  },
  createFromTemplate: async (templateId: string, customizations?: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/workflows/templates/instantiate', { template_id: templateId, ...customizations }));
  },

  // Actions
  listActions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/workflows/actions'));
  },

  // Webhooks
  triggerWebhook: async (workflowId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/webhooks/${workflowId}`, data));
  },

  // Validate workflow
  validateWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/workflows/${workflowId}/validate`));
  },

  // Rollback workflow
  rollbackWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/rollback`));
  },

  // Execute single step
  executeStep: async (workflowId: string, stepIdx: number): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/execute-step/${stepIdx}`));
  },

  // Request approval
  requestApproval: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/workflows/${workflowId}/request-approval`));
  },
};

// ==================== Admin Platform API ====================
export const adminApi = {
  // Custom Role Management
  getRoles: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/roles'));
  },
  createRole: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/admin/roles', data));
  },
  getRole: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/roles/${id}`));
  },
  updateRole: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/admin/roles/${id}`, data));
  },
  deleteRole: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/admin/roles/${id}`));
  },
  getRoleTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/roles/templates'));
  },
  createRoleFromTemplate: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/admin/roles/from-template', data));
  },
  cloneRole: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/roles/${id}/clone`, data));
  },
  getRoleHierarchy: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/roles/${id}/hierarchy`));
  },
  getPermissions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/permissions'));
  },
  getPermissionCategories: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/permissions/categories'));
  },

  // API Key Management
  getApiKeys: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/api-keys', params));
  },
  createApiKey: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/admin/api-keys', data));
  },
  getApiKey: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/api-keys/${id}`));
  },
  revokeApiKey: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/admin/api-keys/${id}`));
  },
  rotateApiKey: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/api-keys/${id}/rotate`));
  },
  suspendApiKey: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/api-keys/${id}/suspend`));
  },
  activateApiKey: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/api-keys/${id}/activate`));
  },
  getApiKeyUsage: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/api-keys/${id}/usage`));
  },

  // Audit Logging
  getAuditEvents: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/audit/events', params));
  },
  getAuditEvent: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/audit/events/${id}`));
  },
  getUserActivity: async (userId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/audit/users/${userId}/activity`));
  },
  getFailedLogins: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/audit/failed-logins', params));
  },
  getAdminActions: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/audit/admin-actions', params));
  },
  getAuditStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/audit/stats'));
  },

  // Workflow Management
  getWorkflows: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/workflows', params));
  },
  createWorkflow: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/admin/workflows', data));
  },
  getWorkflow: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/admin/workflows/${id}`));
  },
  updateWorkflow: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/admin/workflows/${id}`, data));
  },
  approveWorkflow: async (id: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/workflows/${id}/approve`, data));
  },
  rejectWorkflow: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/workflows/${id}/reject`, data));
  },
  executeWorkflow: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/workflows/${id}/execute`));
  },
  cancelWorkflow: async (id: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/admin/workflows/${id}/cancel`, data));
  },
  getWorkflowTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/workflows/templates'));
  },
  createWorkflowFromTemplate: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/admin/workflows/from-template', data));
  },
  getPendingApprovals: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/admin/workflows/pending-approvals'));
  },
};

// ── Projects API (Gap A) ─────────────────────────────────────────────────────
export const projectsApi = {
  list: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/projects'));
  },
  create: async (data: { name: string; description?: string; tags: Record<string, string> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/projects', data));
  },
  delete: async (projectId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/projects/${projectId}`));
  },
  getResources: async (projectId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/projects/${projectId}/resources`));
  },
  getFindings: async (projectId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/projects/${projectId}/findings`));
  },
  getStats: async (projectId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/projects/${projectId}/stats`));
  },
  update: async (projectId: string, data: { name?: string; description?: string; resource_tags?: Record<string, string>; members?: string[]; owner?: string }): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/projects/${projectId}`, data));
  },
};

// ── Report Schedules API (Gap B) ─────────────────────────────────────────────
export const reportSchedulesApi = {
  list: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/reports/schedules'));
  },
  create: async (schedule: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/reports/schedules', schedule));
  },
  delete: async (scheduleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/reports/schedules/${scheduleId}`));
  },
  runNow: async (scheduleId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/reports/schedules/${scheduleId}/run-now`));
  },
  deliveries: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/reports/deliveries'));
  },
};

// ── Auto-Remediation API ─────────────────────────────────────────────────────
export const autoRemediationApi = {
  getConfig: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/auto/config'));
  },
  updateConfig: async (config: any): Promise<any> => {
    return _unwrap(await fetchClient.put('/api/v1/remediation/auto/config', config));
  },
  execute: async (findingIds: string[]): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/remediation/auto/execute', { finding_ids: findingIds }));
  },
  getLog: async (limit?: number): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/remediation/auto/log', { limit }));
  },
};

// ── SSO/SAML/OIDC API ──
export const ssoApi = {
  createProvider: (data: any) => fetchClient.post('/api/v1/auth/sso/providers', data).then(_thenUnwrap),
  listProviders: () => fetchClient.get('/api/v1/auth/sso/providers').then(_thenUnwrap),
  getProvider: (id: string) => fetchClient.get(`/api/v1/auth/sso/providers/${id}`).then(_thenUnwrap),
  updateProvider: (id: string, data: any) => fetchClient.put(`/api/v1/auth/sso/providers/${id}`, data).then(_thenUnwrap),
  deleteProvider: (id: string) => fetchClient.delete(`/api/v1/auth/sso/providers/${id}`).then(_thenUnwrap),
  samlCallback: (data: any) => fetchClient.post('/api/v1/auth/sso/saml/callback', data).then(_thenUnwrap),
  oidcAuthorize: (id: string) => fetchClient.get(`/api/v1/auth/sso/oidc/authorize/${id}`).then(_thenUnwrap),
  oidcCallback: (data: any) => fetchClient.post('/api/v1/auth/sso/oidc/callback', data).then(_thenUnwrap),
  getMetadata: () => fetchClient.get('/api/v1/auth/sso/metadata').then(_thenUnwrap),
};

// ── MFA API ──
export const mfaApi = {
  setup: (userId: string) => fetchClient.post('/api/v1/auth/mfa/setup', { user_id: userId }).then(_thenUnwrap),
  verify: (data: { user_id: string; code: string }) => fetchClient.post('/api/v1/auth/mfa/verify', data).then(_thenUnwrap),
  validate: (data: { user_id: string; code: string }) => fetchClient.post('/api/v1/auth/mfa/validate', data).then(_thenUnwrap),
  remove: (userId: string) => fetchClient.delete(`/api/v1/auth/mfa/${userId}`).then(_thenUnwrap),
  status: (userId: string) => fetchClient.get(`/api/v1/auth/mfa/status/${userId}`).then(_thenUnwrap),
  getPolicy: () => fetchClient.get('/api/v1/auth/mfa/policy').then(_thenUnwrap),
  updatePolicy: (data: any) => fetchClient.put('/api/v1/auth/mfa/policy', data).then(_thenUnwrap),
  generateBackupCodes: (userId: string) => fetchClient.post('/api/v1/auth/mfa/backup-codes', { user_id: userId }).then(_thenUnwrap),
};

// ── Alert Pipeline API ──
export const alertPipelineApi = {
  // Dedup
  getDedupConfig: () => fetchClient.get('/api/v1/notifications/dedup/config').then(_thenUnwrap),
  updateDedupConfig: (data: any) => fetchClient.post('/api/v1/notifications/dedup/config', data).then(_thenUnwrap),
  getDedupStats: () => fetchClient.get('/api/v1/notifications/dedup/stats').then(_thenUnwrap),
  resetDedup: () => fetchClient.delete('/api/v1/notifications/dedup/reset').then(_thenUnwrap),
  // Grouping
  getGroupingConfig: () => fetchClient.get('/api/v1/notifications/grouping/config').then(_thenUnwrap),
  updateGroupingConfig: (data: any) => fetchClient.post('/api/v1/notifications/grouping/config', data).then(_thenUnwrap),
  listGroups: () => fetchClient.get('/api/v1/notifications/grouping/groups').then(_thenUnwrap),
  getGroup: (id: string) => fetchClient.get(`/api/v1/notifications/grouping/groups/${id}`).then(_thenUnwrap),
  // Escalation
  createEscalationPolicy: (data: any) => fetchClient.post('/api/v1/notifications/escalation/policies', data).then(_thenUnwrap),
  listEscalationPolicies: () => fetchClient.get('/api/v1/notifications/escalation/policies').then(_thenUnwrap),
  getEscalationPolicy: (id: string) => fetchClient.get(`/api/v1/notifications/escalation/policies/${id}`).then(_thenUnwrap),
  updateEscalationPolicy: (id: string, data: any) => fetchClient.put(`/api/v1/notifications/escalation/policies/${id}`, data).then(_thenUnwrap),
  deleteEscalationPolicy: (id: string) => fetchClient.delete(`/api/v1/notifications/escalation/policies/${id}`).then(_thenUnwrap),
  acknowledgeAlert: (alertId: string) => fetchClient.post(`/api/v1/notifications/escalation/acknowledge/${alertId}`).then(_thenUnwrap),
  getEscalationStatus: (alertId: string) => fetchClient.get(`/api/v1/notifications/escalation/status/${alertId}`).then(_thenUnwrap),
  // Throttle
  getThrottleConfig: () => fetchClient.get('/api/v1/notifications/throttle/config').then(_thenUnwrap),
  updateThrottleConfig: (data: any) => fetchClient.post('/api/v1/notifications/throttle/config', data).then(_thenUnwrap),
  getThrottleStats: () => fetchClient.get('/api/v1/notifications/throttle/stats').then(_thenUnwrap),
  // Digest
  createDigestConfig: (data: any) => fetchClient.post('/api/v1/notifications/digest/configs', data).then(_thenUnwrap),
  listDigestConfigs: () => fetchClient.get('/api/v1/notifications/digest/configs').then(_thenUnwrap),
  updateDigestConfig: (id: string, data: any) => fetchClient.put(`/api/v1/notifications/digest/configs/${id}`, data).then(_thenUnwrap),
  deleteDigestConfig: (id: string) => fetchClient.delete(`/api/v1/notifications/digest/configs/${id}`).then(_thenUnwrap),
  sendDigestNow: (id: string) => fetchClient.post(`/api/v1/notifications/digest/send-now/${id}`).then(_thenUnwrap),
  previewDigest: (id: string) => fetchClient.get(`/api/v1/notifications/digest/preview/${id}`).then(_thenUnwrap),
  // Maintenance
  createMaintenanceWindow: (data: any) => fetchClient.post('/api/v1/notifications/maintenance', data).then(_thenUnwrap),
  listMaintenanceWindows: () => fetchClient.get('/api/v1/notifications/maintenance').then(_thenUnwrap),
  getActiveWindows: () => fetchClient.get('/api/v1/notifications/maintenance/active').then(_thenUnwrap),
  updateMaintenanceWindow: (id: string, data: any) => fetchClient.put(`/api/v1/notifications/maintenance/${id}`, data).then(_thenUnwrap),
  deleteMaintenanceWindow: (id: string) => fetchClient.delete(`/api/v1/notifications/maintenance/${id}`).then(_thenUnwrap),
  // Pipeline
  processAlert: (data: any) => fetchClient.post('/api/v1/notifications/pipeline/process', data).then(_thenUnwrap),
  processBatch: (alerts: any[]) => fetchClient.post('/api/v1/notifications/pipeline/batch', { alerts }).then(_thenUnwrap),
  getPipelineStatus: () => fetchClient.get('/api/v1/notifications/pipeline/status').then(_thenUnwrap),
};

// ── OAuth Integration API ──
export const oauthApi = {
  createConfig: (data: any) => fetchClient.post('/api/v1/integrations/oauth/configs', data).then(_thenUnwrap),
  listConfigs: () => fetchClient.get('/api/v1/integrations/oauth/configs').then(_thenUnwrap),
  deleteConfig: (id: string) => fetchClient.delete(`/api/v1/integrations/oauth/configs/${id}`).then(_thenUnwrap),
  authorize: (id: string) => fetchClient.get(`/api/v1/integrations/oauth/authorize/${id}`).then(_thenUnwrap),
  callback: (data: { code: string; state: string }) => fetchClient.post('/api/v1/integrations/oauth/callback', data).then(_thenUnwrap),
  refresh: (id: string) => fetchClient.post(`/api/v1/integrations/oauth/refresh/${id}`).then(_thenUnwrap),
  getStatus: (id: string) => fetchClient.get(`/api/v1/integrations/oauth/status/${id}`).then(_thenUnwrap),
};

// ── R140-R144: Custom Dashboards v1 API ──
export const customDashboardsApi = {
  list: () => fetchClient.get('/api/v1/dashboards/custom').then(_thenUnwrap),
  get: (id: string) => fetchClient.get(`/api/v1/dashboards/custom/${id}`).then(_thenUnwrap),
  create: (data: { name: string; description?: string; layout?: any[]; widgets?: any[]; is_shared?: boolean }) =>
    fetchClient.post('/api/v1/dashboards/custom', data).then(_thenUnwrap),
  update: (id: string, data: { name?: string; description?: string; layout?: any[]; widgets?: any[]; is_shared?: boolean }) =>
    fetchClient.put(`/api/v1/dashboards/custom/${id}`, data).then(_thenUnwrap),
  delete: (id: string) => fetchClient.delete(`/api/v1/dashboards/custom/${id}`).then(_thenUnwrap),
  setDefault: (id: string) => fetchClient.post(`/api/v1/dashboards/custom/${id}/set-default`, {}).then(_thenUnwrap),
  getWidgetCatalog: () => fetchClient.get('/api/v1/dashboards/widgets/catalog').then(_thenUnwrap),
};

// ── R85: Multi-tenant Org Hierarchy API ──
export const tenantsApi = {
  list: () => fetchClient.get('/api/v1/admin/tenants').then(_thenUnwrap),
  create: (data: { name: string; parent_id?: string; description?: string; tags?: string[] }) =>
    fetchClient.post('/api/v1/admin/tenants', data).then(_thenUnwrap),
  getMembers: (tenantId: string) => fetchClient.get(`/api/v1/admin/tenants/${tenantId}/members`).then(_thenUnwrap),
  getPolicies: (tenantId: string) => fetchClient.get(`/api/v1/admin/tenants/${tenantId}/policies`).then(_thenUnwrap),
  // R151 — payload shape matches BE (admin-service/tenants.py:push_policy_to_members)
  pushPolicy: (
    tenantId: string,
    data: {
      policy_name: string;
      policy_type?: 'mandatory' | 'recommended';
      policy_data?: any;
      target_groups?: string[];
    },
  ) =>
    fetchClient.post(`/api/v1/admin/tenants/${tenantId}/push-policy`, data).then(_thenUnwrap),
  authorize: (tenantId: string, data: { user_id: string; role?: string }) =>
    fetchClient.post(`/api/v1/admin/tenants/${tenantId}/authorize`, data).then(_thenUnwrap),
  disconnect: (tenantId: string) => fetchClient.post(`/api/v1/admin/tenants/${tenantId}/disconnect`, {}).then(_thenUnwrap),
  seedDefaults: (tenantId: string) => fetchClient.post(`/api/v1/admin/tenants/${tenantId}/seed-defaults`, {}).then(_thenUnwrap),
  exportTenant: (tenantId: string) => fetchClient.post(`/api/v1/admin/tenants/${tenantId}/export`, {}).then(_thenUnwrap),
  // R193-SHIP-O 2026-05-19 — per-tenant blast-radius rollup
  // (resource_count, issue_count, p0_count, unresolved_count, ...).
  // BE: services/platform/admin-service/app/routers/tenants_blast_radius.py
  blastRadiusSummary: (
    params?: { limit?: number; sort_by?: string },
  ) => fetchClient.get('/api/v1/admin/tenants/blast-radius-summary', params as any).then(_thenUnwrap),
};

// ── R84: Billing / License API ──
export const billingApi = {
  getPlans: () => fetchClient.get('/api/v1/plans').then(_thenUnwrap),
  getMySubscription: () => fetchClient.get('/api/v1/subscriptions/me').then(_thenUnwrap),
  setSubscription: (data: { plan_id: string; status?: string; trial_ends_at?: string; payment_provider?: string; payment_provider_id?: string }) =>
    fetchClient.post('/api/v1/subscriptions/me', data).then(_thenUnwrap),
  getMyUsage: (params?: { since?: string }) => fetchClient.get('/api/v1/usage/me', params as any).then(_thenUnwrap),
  getMyQuota: () => fetchClient.get('/api/v1/usage/me/quota').then(_thenUnwrap),
  recordUsage: (data: { event_type: string; quantity?: number; metadata?: any }) =>
    fetchClient.post('/api/v1/usage/record', data).then(_thenUnwrap),
};

// ── Scoped API Keys ──
export const apiKeysApi = {
    create: (data: any) => fetchClient.post('/api/v1/auth/api-keys', data).then(_thenUnwrap),
    list: () => fetchClient.get('/api/v1/auth/api-keys').then(_thenUnwrap),
    get: (id: string) => fetchClient.get(`/api/v1/auth/api-keys/${id}`).then(_thenUnwrap),
    update: (id: string, data: any) => fetchClient.put(`/api/v1/auth/api-keys/${id}`, data).then(_thenUnwrap),
    revoke: (id: string) => fetchClient.delete(`/api/v1/auth/api-keys/${id}`).then(_thenUnwrap),
    rotate: (id: string) => fetchClient.post(`/api/v1/auth/api-keys/${id}/rotate`).then(_thenUnwrap),
    validate: (key: string) => fetchClient.post('/api/v1/auth/api-keys/validate', { key }).then(_thenUnwrap),
    getScopes: () => fetchClient.get('/api/v1/auth/api-keys/scopes').then(_thenUnwrap),
};

// ── SSO Group-to-Role Auto-Mapping ──
export const ssoGroupMappingApi = {
    create: (data: any) => fetchClient.post('/api/v1/auth/sso/group-mappings', data).then(_thenUnwrap),
    list: () => fetchClient.get('/api/v1/auth/sso/group-mappings').then(_thenUnwrap),
    get: (id: string) => fetchClient.get(`/api/v1/auth/sso/group-mappings/${id}`).then(_thenUnwrap),
    update: (id: string, data: any) => fetchClient.put(`/api/v1/auth/sso/group-mappings/${id}`, data).then(_thenUnwrap),
    delete: (id: string) => fetchClient.delete(`/api/v1/auth/sso/group-mappings/${id}`).then(_thenUnwrap),
    test: (groups: string[]) => fetchClient.post('/api/v1/auth/sso/group-mappings/test', { groups }).then(_thenUnwrap),
    getSyncStatus: () => fetchClient.get('/api/v1/auth/sso/group-mappings/sync-status').then(_thenUnwrap),
};

// ============================================================================
// Admin Service - Users, Settings, Backups, Encryption, MFA Status
// ============================================================================

export const adminUsersApi = {
  list: (params?: { page?: number; page_size?: number; role?: string; status?: string }) =>
    fetchClient.get('/api/v1/admin/users', params).then(_thenUnwrap),
  get: (id: string) =>
    fetchClient.get(`/api/v1/admin/users/${id}`).then(_thenUnwrap),
};

export const adminSettingsApi = {
  get: () =>
    fetchClient.get('/api/v1/admin/settings').then(_thenUnwrap),
  update: (data: any) =>
    fetchClient.put('/api/v1/admin/settings', data).then(_thenUnwrap),
};

export const adminBackupsApi = {
  list: () =>
    fetchClient.get('/api/v1/admin/backups').then(_thenUnwrap),
  create: (data?: { description?: string }) =>
    fetchClient.post('/api/v1/admin/backups', data).then(_thenUnwrap),
  getSchedule: () =>
    fetchClient.get('/api/v1/admin/backup/schedule').then(_thenUnwrap),
  updateSchedule: (data: { enabled: boolean; cron?: string; retention_days?: number }) =>
    fetchClient.put('/api/v1/admin/backup/schedule', data).then(_thenUnwrap),
};

export const adminEncryptionApi = {
  getStatus: () =>
    fetchClient.get('/api/v1/admin/encryption/status').then(_thenUnwrap),
  rotateKey: () =>
    fetchClient.post('/api/v1/admin/encryption/rotate-key').then(_thenUnwrap),
  encrypt: (data: { plaintext: string; key_id?: string }) =>
    fetchClient.post('/api/v1/admin/encryption/encrypt', data).then(_thenUnwrap),
  decrypt: (data: { ciphertext: string; key_id?: string }) =>
    fetchClient.post('/api/v1/admin/encryption/decrypt', data).then(_thenUnwrap),
  generateKey: (data?: { key_type?: string; description?: string }) =>
    fetchClient.post('/api/v1/admin/encryption/generate-key', data).then(_thenUnwrap),
};

// Admin Audit Trail
export const adminAuditApi = {
  getEvents: (params?: { event_type?: string; user_id?: string; start_date?: string; end_date?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/admin/audit/events', params).then(_thenUnwrap),
  getEvent: (eventId: string) =>
    fetchClient.get(`/api/v1/admin/audit/events/${eventId}`).then(_thenUnwrap),
  getUserActivity: (userId: string, params?: { limit?: number }) =>
    fetchClient.get(`/api/v1/admin/audit/users/${userId}/activity`, params).then(_thenUnwrap),
  getFailedLogins: (params?: { start_date?: string; end_date?: string; limit?: number }) =>
    fetchClient.get('/api/v1/admin/audit/failed-logins', params).then(_thenUnwrap),
  getAdminActions: (params?: { action_type?: string; limit?: number }) =>
    fetchClient.get('/api/v1/admin/audit/admin-actions', params).then(_thenUnwrap),
  getStats: (params?: { period?: string }) =>
    fetchClient.get('/api/v1/admin/audit/stats', params).then(_thenUnwrap),
};

// Admin Workflows (change management)
export const adminWorkflowsApi = {
  getTemplates: () =>
    fetchClient.get('/api/v1/admin/workflows/templates').then(_thenUnwrap),
  createFromTemplate: (data: any) =>
    fetchClient.post('/api/v1/admin/workflows/from-template', data).then(_thenUnwrap),
  getPendingApprovals: () =>
    fetchClient.get('/api/v1/admin/workflows/pending-approvals').then(_thenUnwrap),
  list: (params?: { status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/admin/workflows', params).then(_thenUnwrap),
  create: (data: any) =>
    fetchClient.post('/api/v1/admin/workflows', data).then(_thenUnwrap),
  get: (workflowId: string) =>
    fetchClient.get(`/api/v1/admin/workflows/${workflowId}`).then(_thenUnwrap),
  update: (workflowId: string, data: any) =>
    fetchClient.put(`/api/v1/admin/workflows/${workflowId}`, data).then(_thenUnwrap),
  approve: (workflowId: string, data?: { comment?: string }) =>
    fetchClient.post(`/api/v1/admin/workflows/${workflowId}/approve`, data).then(_thenUnwrap),
  reject: (workflowId: string, data?: { reason?: string }) =>
    fetchClient.post(`/api/v1/admin/workflows/${workflowId}/reject`, data).then(_thenUnwrap),
  execute: (workflowId: string) =>
    fetchClient.post(`/api/v1/admin/workflows/${workflowId}/execute`).then(_thenUnwrap),
  cancel: (workflowId: string) =>
    fetchClient.post(`/api/v1/admin/workflows/${workflowId}/cancel`).then(_thenUnwrap),
};

// Admin Custom Roles
export const adminCustomRolesApi = {
  create: (data: any) =>
    fetchClient.post('/api/v1/admin/roles/custom', data).then(_thenUnwrap),
  list: () =>
    fetchClient.get('/api/v1/admin/roles/custom').then(_thenUnwrap),
  get: (roleId: string) =>
    fetchClient.get(`/api/v1/admin/roles/custom/${roleId}`).then(_thenUnwrap),
  update: (roleId: string, data: any) =>
    fetchClient.put(`/api/v1/admin/roles/custom/${roleId}`, data).then(_thenUnwrap),
  delete: (roleId: string) =>
    fetchClient.delete(`/api/v1/admin/roles/custom/${roleId}`).then(_thenUnwrap),
  assignRole: (data: { user_id: string; role_id: string }) =>
    fetchClient.post('/api/v1/admin/roles/assignments', data).then(_thenUnwrap),
  getAssignments: (params?: { role_id?: string; user_id?: string }) =>
    fetchClient.get('/api/v1/admin/roles/assignments', params).then(_thenUnwrap),
};

// SSO Providers
export const ssoProvidersApi = {
  list: () =>
    fetchClient.get('/api/v1/auth/sso/providers').then(_thenUnwrap),
  create: (data: any) =>
    fetchClient.post('/api/v1/auth/sso/providers', data).then(_thenUnwrap),
  get: (providerId: string) =>
    fetchClient.get(`/api/v1/auth/sso/providers/${providerId}`).then(_thenUnwrap),
  update: (providerId: string, data: any) =>
    fetchClient.put(`/api/v1/auth/sso/providers/${providerId}`, data).then(_thenUnwrap),
  delete: (providerId: string) =>
    fetchClient.delete(`/api/v1/auth/sso/providers/${providerId}`).then(_thenUnwrap),
  getMetadata: () =>
    fetchClient.get('/api/v1/auth/sso/metadata').then(_thenUnwrap),
};

// MFA Management (enhanced)
export const mfaManagementApi = {
  setup: (data: { user_id: string; method?: string }) =>
    fetchClient.post('/api/v1/auth/mfa/setup', data).then(_thenUnwrap),
  verify: (data: { user_id: string; code: string }) =>
    fetchClient.post('/api/v1/auth/mfa/verify', data).then(_thenUnwrap),
  validate: (data: { user_id: string; code: string }) =>
    fetchClient.post('/api/v1/auth/mfa/validate', data).then(_thenUnwrap),
  disable: (userId: string) =>
    fetchClient.delete(`/api/v1/auth/mfa/${userId}`).then(_thenUnwrap),
  getPolicy: () =>
    fetchClient.get('/api/v1/auth/mfa/policy').then(_thenUnwrap),
  updatePolicy: (data: any) =>
    fetchClient.put('/api/v1/auth/mfa/policy', data).then(_thenUnwrap),
  generateBackupCodes: (data: { user_id: string }) =>
    fetchClient.post('/api/v1/auth/mfa/backup-codes', data).then(_thenUnwrap),
};

// Admin SIEM Integration
export const adminSiemApi = {
  createConfig: (data: any) =>
    fetchClient.post('/api/v1/audit/siem/configs', data).then(_thenUnwrap),
  getConfigs: () =>
    fetchClient.get('/api/v1/audit/siem/configs').then(_thenUnwrap),
  getConfig: (configId: string) =>
    fetchClient.get(`/api/v1/audit/siem/configs/${configId}`).then(_thenUnwrap),
  updateConfig: (configId: string, data: any) =>
    fetchClient.put(`/api/v1/audit/siem/configs/${configId}`, data).then(_thenUnwrap),
  deleteConfig: (configId: string) =>
    fetchClient.delete(`/api/v1/audit/siem/configs/${configId}`).then(_thenUnwrap),
};

export const mfaStatusApi = {
  getStatus: (userId: string) =>
    fetchClient.get(`/api/v1/auth/mfa/status/${userId}`).then(_thenUnwrap),
};

// ============================================================================
// Inventory Collection (Stage 2) — R76
// Per-account collection status, cancel, retry. Backed by scan-orchestrator
// /api/v1/scans/* + /api/v1/inventory/collection-status/{account_id}.
// ============================================================================

export interface CollectionScan {
  scan_id: string;
  status: string;
  scan_type: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_resources: number;
  total_findings: number;
  status_message: string | null;
  regions: string[] | null;
  services: string[] | null;
  triggered_by: string | null;
  duration_seconds: number | null;
}

export interface CollectionStatus {
  account_id: string;
  last_collection: CollectionScan | null;
  active_collection: CollectionScan | null;
  history: CollectionScan[];
  resource_counts: {
    total: number;
    by_type: Record<string, number>;
    by_provider: Record<string, number>;
  };
}

/** One finding as reported by GET /api/v1/scans/{id}/findings. */
export interface ScanDeltaFinding {
  id: string;
  title: string | null;
  severity: string | null;
  status: string | null;
  resource_id: string | null;
  region: string | null;
  account_id: string | null;
  rule_id: string | null;
  delta: 'new' | 'recurring' | 'resolved';
}

/**
 * What a single scan actually changed.
 *
 * `total_findings` on the scan row is the account's cumulative open count, so it
 * cannot answer "what did THIS scan find" — a no-op scan and one that surfaced
 * 200 new criticals report the same number. Computed per-scan from
 * csp_core.finding_scan_observations instead.
 *
 * `resolved` is null (not 0) when the scan has no predecessor: nothing COULD
 * have been resolved, which is a different statement from "0 were".
 */
export interface ScanFindingsDelta {
  scan_id: string;
  account_id: string;
  status: string;
  previous_scan_id: string | null;
  summary: {
    observed: number;
    new: number;
    recurring: number;
    resolved: number | null;
  };
  delta: string;
  total: number;
  limit: number;
  offset: number;
  findings: ScanDeltaFinding[];
}

export const inventoryCollectionApi = {
  /** New / recurring / resolved for one scan, vs the account's previous scan. */
  getScanFindings: (
    scanId: string,
    opts?: { delta?: 'all' | 'new' | 'recurring' | 'resolved'; limit?: number; offset?: number },
  ): Promise<ScanFindingsDelta> =>
    fetchClient
      .get(`/api/v1/scans/${encodeURIComponent(scanId)}/findings`, {
        ...(opts?.delta ? { delta: opts.delta } : {}),
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
        ...(opts?.offset != null ? { offset: opts.offset } : {}),
      })
      .then(_thenUnwrap),
  getStatus: (accountId: string, historyLimit?: number): Promise<CollectionStatus> =>
    fetchClient
      .get(
        `/api/v1/inventory/collection-status/${encodeURIComponent(accountId)}`,
        historyLimit ? { history_limit: historyLimit } : undefined,
      )
      .then(_thenUnwrap),
  cancelScan: (scanId: string) =>
    fetchClient.post(`/api/v1/scans/${encodeURIComponent(scanId)}/cancel`).then(_thenUnwrap),
  retryScan: (scanId: string) =>
    fetchClient.post(`/api/v1/scans/${encodeURIComponent(scanId)}/retry`).then(_thenUnwrap),
  triggerScan: (accountId: string, opts?: { regions?: string[]; scan_type?: string }) =>
    fetchClient
      .post('/api/v1/scans', {
        account_id: accountId,
        scan_type: opts?.scan_type || 'full',
        regions: opts?.regions || [],
        triggered_by: 'manual:inventory-dashboard',
      })
      .then(_thenUnwrap),
};

// ============================================================================
// Tenant Manager API
// ============================================================================

export const tenantApi = {
  create: (data: any) => fetchClient.post('/api/v1/tenants', data).then(_thenUnwrap),
  list: (params?: any) => fetchClient.get('/api/v1/tenants', params).then(_thenUnwrap),
  get: (id: string) => fetchClient.get(`/api/v1/tenants/${id}`).then(_thenUnwrap),
  update: (id: string, data: any) => fetchClient.put(`/api/v1/tenants/${id}`, data).then(_thenUnwrap),
  delete: (id: string) => fetchClient.delete(`/api/v1/tenants/${id}`).then(_thenUnwrap),
  getDashboard: () => fetchClient.get('/api/v1/tenants/dashboard').then(_thenUnwrap),
  getComparison: () => fetchClient.get('/api/v1/tenants/comparison').then(_thenUnwrap),
  getChampionCenter: () => fetchClient.get('/api/v1/tenants/champion-center').then(_thenUnwrap),
  getOnboarding: (id: string) => fetchClient.get(`/api/v1/tenants/${id}/onboarding`).then(_thenUnwrap),
  enforcePolicies: (id: string) => fetchClient.post(`/api/v1/tenants/${id}/enforce-policies`).then(_thenUnwrap),
  createGlobalPolicy: (data: any) => fetchClient.post('/api/v1/tenants/global-policies', data).then(_thenUnwrap),
  getGlobalPolicies: () => fetchClient.get('/api/v1/tenants/global-policies').then(_thenUnwrap),
};
